import { Http } from '@/lib/http';

export class NotificationService extends Http {
  constructor() {
    super();
    this.endpoint = 'notification-api'
    this.notificationEndpoint = 'delinquency-pro/app/notifications'
  }
  
  sendNotification(details) {
    return this.httpClient().post(`${this.endpoint}`, details)
  }
  
  /**
   * Get all available modules with their notification settings
   */
  getAvailableModules() {
    return this.httpClient().get(`${this.notificationEndpoint}/modules`)
  }

  /**
   * Get notification settings for a specific module
   */
  getNotificationSettings(module) {
    return this.httpClient().get(`${this.notificationEndpoint}/settings/${module}`)
  }

  /**
   * Send custom notification from UI
   */
  sendCustomNotification(data) {
    return this.httpClient().post(`${this.notificationEndpoint}/send`, data)
  }

  /**
   * Preview notification template with sample data
   */
  previewNotification(data) {
    return this.httpClient().post(`${this.notificationEndpoint}/preview`, data)
  }

  // ======================================
  // NOTIFICATION HISTORY METHODS
  // ======================================

  /**
   * Get notification history with pagination and filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @param {string} params.status - Filter by status ('success', 'failed', 'pending')
   * @param {string} params.module - Filter by module
   * @param {string} params.process - Filter by process
   * @param {string} params.type - Filter by type ('email', 'sms')
   * @param {string} params.dateFrom - Filter from date (YYYY-MM-DD)
   * @param {string} params.dateTo - Filter to date (YYYY-MM-DD)
   * @param {string} params.sortBy - Sort field (default: 'timestamp')
   * @param {string} params.sortOrder - Sort order ('asc', 'desc')
   */
  getNotificationHistory(params = {}) {
    const searchParams = new URLSearchParams();
    
    // Add parameters if they exist
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value);
      }
    });

    const queryString = searchParams.toString();
    const url = `${this.notificationEndpoint}/history${queryString ? `?${queryString}` : ''}`;
    
    return this.httpClient().get(url);
  }

  /**
   * Get notification by ID
   * @param {string} id - Notification ID
   */
  getNotificationById(id) {
    return this.httpClient().get(`${this.notificationEndpoint}/history/${id}`)
  }

  /**
   * Get notification statistics
   * @param {Object} filters - Filter parameters
   * @param {string} filters.module - Filter by module
   * @param {string} filters.process - Filter by process
   * @param {string} filters.dateFrom - Filter from date
   * @param {string} filters.dateTo - Filter to date
   */
  getNotificationStatistics(filters = {}) {
    const searchParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value);
      }
    });

    const queryString = searchParams.toString();
    const url = `${this.notificationEndpoint}/history/statistics${queryString ? `?${queryString}` : ''}`;
    
    return this.httpClient().get(url);
  }

  /**
   * Get unique modules from notification history
   */
  getHistoryModules() {
    return this.httpClient().get(`${this.notificationEndpoint}/history/modules`)
  }

  /**
   * Get unique processes from notification history
   * @param {string} module - Optional module filter
   */
  getHistoryProcesses(module = null) {
    const url = module 
      ? `${this.notificationEndpoint}/history/processes?module=${encodeURIComponent(module)}`
      : `${this.notificationEndpoint}/history/processes`;
    
    return this.httpClient().get(url);
  }

  /**
   * Get failed notifications for retry
   * @param {number} limit - Maximum number of records to return (default: 50)
   */
  getFailedNotifications(limit = 50) {
    return this.httpClient().get(`${this.notificationEndpoint}/history/failed?limit=${limit}`)
  }

  /**
   * Retry a failed notification
   * @param {string} id - Notification ID to retry
   */
  retryNotification(id) {
    return this.httpClient().post(`${this.notificationEndpoint}/history/${id}/retry`)
  }

  /**
   * Bulk retry multiple failed notifications
   * @param {string[]} ids - Array of notification IDs to retry
   */
  bulkRetryNotifications(ids) {
    return this.httpClient().post(`${this.notificationEndpoint}/history/bulk-retry`, { ids })
  }

  /**
   * Bulk update notification statuses
   * @param {Object} data - Update data
   * @param {string[]} data.ids - Array of notification IDs
   * @param {string} data.status - New status ('success', 'failed', 'pending')
   * @param {string} data.errorMessage - Optional error message
   */
  bulkUpdateStatus(data) {
    return this.httpClient().put(`${this.notificationEndpoint}/history/bulk-status`, data)
  }

  /**
   * Delete old notification history records
   * @param {number} days - Delete records older than this many days (default: 90)
   */
  cleanupOldNotifications(days = 90) {
    return this.httpClient().delete(`${this.notificationEndpoint}/history/cleanup?days=${days}`)
  }

  // ======================================
  // CONVENIENCE METHODS FOR COMMON UI SCENARIOS
  // ======================================

  /**
   * Get recent notifications (last 7 days)
   * @param {number} limit - Number of records to return
   */
  getRecentNotifications(limit = 20) {
    const dateFrom = new Date();
    dateFrom.setDate(dateFrom.getDate() - 7);
    
    return this.getNotificationHistory({
      limit,
      dateFrom: dateFrom.toISOString().split('T')[0],
      sortBy: 'timestamp',
      sortOrder: 'desc'
    });
  }

  /**
   * Get today's notifications
   */
  getTodaysNotifications() {
    const today = new Date().toISOString().split('T')[0];
    
    return this.getNotificationHistory({
      dateFrom: today,
      dateTo: today,
      sortBy: 'timestamp',
      sortOrder: 'desc'
    });
  }

  /**
   * Get notifications by date range
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {Object} additionalFilters - Additional filter parameters
   */
  getNotificationsByDateRange(startDate, endDate, additionalFilters = {}) {
    return this.getNotificationHistory({
      dateFrom: startDate,
      dateTo: endDate,
      sortBy: 'timestamp',
      sortOrder: 'desc',
      ...additionalFilters
    });
  }

  /**
   * Get notifications by status with pagination
   * @param {string} status - Status to filter by
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   */
  getNotificationsByStatus(status, page = 1, limit = 20) {
    return this.getNotificationHistory({
      status,
      page,
      limit,
      sortBy: 'timestamp',
      sortOrder: 'desc'
    });
  }

  /**
   * Get notifications by module and process
   * @param {string} module - Module name
   * @param {string} process - Process name (optional)
   * @param {Object} additionalFilters - Additional filters
   */
  getNotificationsByModuleProcess(module, process = null, additionalFilters = {}) {
    const filters = {
      module,
      sortBy: 'timestamp',
      sortOrder: 'desc',
      ...additionalFilters
    };

    if (process) {
      filters.process = process;
    }

    return this.getNotificationHistory(filters);
  }

  /**
   * Search notifications by multiple criteria
   * @param {Object} searchCriteria - Search parameters
   */
  searchNotifications(searchCriteria) {
    return this.getNotificationHistory({
      sortBy: 'timestamp',
      sortOrder: 'desc',
      ...searchCriteria
    });
  }

  /**
   * Get dashboard summary data
   * @param {number} days - Number of days to look back (default: 30)
   */
  async getDashboardSummary(days = 30) {
    const dateFrom = new Date();
    dateFrom.setDate(dateFrom.getDate() - days);

    try {
      const [statistics, recentFailures] = await Promise.all([
        this.getNotificationStatistics({
          dateFrom: dateFrom.toISOString().split('T')[0]
        }),
        this.getFailedNotifications(10)
      ]);

      return {
        success: true,
        data: {
          statistics: statistics.data,
          recentFailures: recentFailures.data,
          period: `Last ${days} days`
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to get dashboard summary'
      };
    }
  }

  addUserNotification(recieverID, module, notification) {
    return this.httpClient().patch(`${this.notificationEndpoint}/userNotificationHistory`, { notification, module, recieverID })
  }

  deleteNotifications(userId, ids) {
    return this.httpClient().delete(`${this.notificationEndpoint}/userNotificationHistory`, { data: { userId, ids } })
  }

  loadUserNotificationHistroy(userId, moduleName) {
    return this.httpClient().get(`${this.notificationEndpoint}/userNotificationHistory/${moduleName}/${userId}`)
  }
}